import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import type { Audit } from '@/types/asset-audit';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type GroupBy = 'none' | 'dueDate' | 'type' | 'priority' | 'status';

// Helper function to get user initials - extracted to reduce nesting depth (S2004)
function getUserInitials(userName: string): string {
  return userName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase();
}

interface AuditsListViewProps {
  readonly audits: Audit[];
  readonly loading?: boolean;
  readonly onAuditClick: (auditId: string) => void;
  readonly selectedRowIds: Set<string>;
  readonly onSelectionChange: (auditId: string, checked: boolean) => void;
  readonly groupBy: GroupBy;
  readonly visibleColumns?: string[];
}

export function AuditsListView({ 
  audits, 
  loading, 
  onAuditClick, 
  selectedRowIds, 
  onSelectionChange,
  groupBy,
  visibleColumns = ['title', 'type', 'status', 'priority', 'dueDate', 'progress', 'assignedUsers']
}: Readonly<AuditsListViewProps>) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  
  // Helper to check if a column is visible
  const isColumnVisible = (columnId: string) => visibleColumns.includes(columnId);

  // Helper to get due date icon color class - extracted to avoid nested function depth (S2004)
  const getDueDateIconColor = (dueDate: { isOverdue: boolean; isToday: boolean }) => {
    if (dueDate.isOverdue) return 'text-red-600';
    if (dueDate.isToday) return 'text-orange-600';
    return 'text-muted-foreground';
  };

  // Helper to get due date text color class - extracted to avoid nested function depth (S2004)
  const getDueDateTextColor = (dueDate: { isOverdue: boolean; isToday: boolean }) => {
    if (dueDate.isOverdue) return 'text-red-600 font-medium';
    if (dueDate.isToday) return 'text-orange-600 font-medium';
    return 'text-muted-foreground';
  };

  const handleSelectAll = (checked: boolean) => {
    audits.forEach(audit => {
      onSelectionChange(audit.id, checked);
    });
  };

  const toggleGroupCollapse = (groupLabel: string) => {
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(groupLabel)) {
      newCollapsed.delete(groupLabel);
    } else {
      newCollapsed.add(groupLabel);
    }
    setCollapsedGroups(newCollapsed);
  };

  const getGroupLabel = (audit: Audit, groupBy: GroupBy): string => {
    switch (groupBy) {
      case 'dueDate': {
        if (!audit.scheduledEndDate) return 'No Due Date';
        const dueDate = new Date(audit.scheduledEndDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);
        
        if (dueDate < today) return 'Overdue';
        if (dueDate.toDateString() === today.toDateString()) return 'Today';
        if (dueDate < nextWeek) return 'This Week';
        return 'Upcoming';
      }
      case 'type':
        return audit.type ?? 'Unknown Type';
      
      case 'priority':
        return audit.priority ?? 'No Priority';
      
      case 'status':
        return audit.status ?? 'No Status';
      
      default:
        return 'All Audits';
    }
  };

  const groupAudits = (audits: Audit[], groupBy: GroupBy): Record<string, Audit[]> => {
    if (groupBy === 'none') {
      return { 'All Audits': audits };
    }

    const groups: Record<string, Audit[]> = {};
    audits.forEach(audit => {
      const label = getGroupLabel(audit, groupBy);
      if (!groups[label]) {
        groups[label] = [];
      }
      groups[label].push(audit);
    });

    // Sort groups by priority for dueDate grouping
    if (groupBy === 'dueDate') {
      const order = ['Overdue', 'Today', 'This Week', 'Upcoming', 'No Due Date'];
      const sortedGroups: Record<string, Audit[]> = {};
      order.forEach(key => {
        if (groups[key]) {
          sortedGroups[key] = groups[key];
        }
      });
      return sortedGroups;
    }

    return groups;
  };

  const groupedAudits = groupAudits(audits, groupBy);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Draft': return 'bg-slate-100 text-slate-800';
      case 'Scheduled': return 'bg-blue-100 text-blue-800';
      case 'Assigned': return 'bg-cyan-100 text-cyan-800';
      case 'InProgress': return 'bg-purple-100 text-purple-800';
      case 'UnderReview': return 'bg-orange-100 text-orange-800';
      case 'OnHold': return 'bg-yellow-100 text-yellow-800';
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'Cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDueDate = (dueDate?: string) => {
    if (!dueDate) return null;
    
    const date = new Date(dueDate);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const isOverdue = date < today;
    const isToday = date.toDateString() === today.toDateString();
    
    let label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (isToday) label = 'Today';
    
    return {
      label,
      isOverdue,
      isToday,
    };
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        {[...Array(10)].map((_, i) => (
          <Skeleton key={`skeleton-${i}`} className="h-16" />
        ))}
      </div>
    );
  }

  if (!audits || audits.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-lg font-medium text-muted-foreground">No audits found</p>
          <p className="text-sm text-muted-foreground mt-1">
            Try adjusting your filters or create a new audit
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
          <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
            <TableHead className="h-12 font-semibold w-[60px]">
              <div className="flex items-center justify-center">
                <Checkbox
                  checked={audits && selectedRowIds.size === audits.length && audits.length > 0}
                  onCheckedChange={handleSelectAll}
                />
              </div>
            </TableHead>
            {isColumnVisible('title') && (
              <TableHead className="h-12 font-semibold min-w-[250px]">Audit</TableHead>
            )}
            {isColumnVisible('type') && (
              <TableHead className="h-12 font-semibold w-[150px]">Type</TableHead>
            )}
            {isColumnVisible('status') && (
              <TableHead className="h-12 font-semibold w-[120px]">Status</TableHead>
            )}
            {isColumnVisible('priority') && (
              <TableHead className="h-12 font-semibold w-[120px]">Priority</TableHead>
            )}
            {isColumnVisible('dueDate') && (
              <TableHead className="h-12 font-semibold w-[120px]">Due Date</TableHead>
            )}
            {isColumnVisible('progress') && (
              <TableHead className="h-12 font-semibold w-[200px]">Progress</TableHead>
            )}
            {isColumnVisible('assignedUsers') && (
              <TableHead className="h-12 font-semibold w-[180px]">Assignees</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.entries(groupedAudits).map(([groupLabel, groupAudits]) => (
            <>
              {/* Group Header Row */}
              {groupBy !== 'none' && (
                <TableRow 
                  key={`group-${groupLabel}`}
                  className="bg-muted/50 hover:bg-muted/70 cursor-pointer border-b-2 border-slate-200 dark:border-slate-700"
                  onClick={() => toggleGroupCollapse(groupLabel)}
                >
                  <TableCell colSpan={visibleColumns.length + 1} className="py-2">
                    <div className="flex items-center gap-2">
                      {collapsedGroups.has(groupLabel) ? (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="text-sm font-semibold">{groupLabel}</span>
                      <Badge variant="secondary" className="text-xs">
                        {groupAudits.length}
                      </Badge>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              
              {/* Group Rows */}
              {!collapsedGroups.has(groupLabel) && groupAudits.map((audit, index) => {
                const dueDate = formatDueDate(audit.scheduledEndDate);
                const progressPercentage = audit.totalAssets > 0 
                  ? Math.round((audit.completedAssets / audit.totalAssets) * 100) 
                  : 0;
                
                return (
                  <TableRow
                    key={audit.id}
                    className={`
                      ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                      hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800 cursor-pointer
                    `}
                    onClick={() => onAuditClick(audit.id)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center">
                        <Checkbox
                          checked={selectedRowIds.has(audit.id)}
                          onCheckedChange={(checked) => onSelectionChange(audit.id, checked as boolean)}
                        />
                      </div>
                    </TableCell>
                    {isColumnVisible('title') && (
                      <TableCell className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="font-medium text-primary hover:underline cursor-pointer">
                            {audit.name || audit.title}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            #{audit.auditNumber}
                          </div>
                        </div>
                      </TableCell>
                    )}
                    {isColumnVisible('type') && (
                      <TableCell className="px-4 py-3">
                        <span className="text-sm text-muted-foreground">{audit.type}</span>
                      </TableCell>
                    )}
                    {isColumnVisible('status') && (
                      <TableCell className="px-4 py-3">
                        <Badge className={getStatusColor(audit.status)}>
                          {audit.status.replace(/([A-Z])/g, ' $1').trim()}
                        </Badge>
                      </TableCell>
                    )}
                    {isColumnVisible('priority') && (
                      <TableCell className="px-4 py-3">
                        <Badge variant="outline">
                          {audit.priority}
                        </Badge>
                      </TableCell>
                    )}
                    {isColumnVisible('dueDate') && (
                      <TableCell className="px-4 py-3">
                        {dueDate ? (
                          <div className="flex items-center gap-2">
                                <Calendar className={cn(
                                  'h-3 w-3',
                                  getDueDateIconColor(dueDate)
                                )} />
                            <span className={cn(
                              'text-sm',
                              getDueDateTextColor(dueDate)
                            )}>
                              {dueDate.label}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-500 dark:text-slate-400">No due date</span>
                        )}
                      </TableCell>
                    )}
                    {isColumnVisible('progress') && (
                      <TableCell className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary transition-all"
                                style={{ width: `${progressPercentage}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {progressPercentage}%
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {audit.completedAssets} / {audit.totalAssets} assets
                          </div>
                        </div>
                      </TableCell>
                    )}
                    {isColumnVisible('assignedUsers') && (
                      <TableCell className="px-4 py-3">
                        {audit.assignedUsers && audit.assignedUsers.length > 0 ? (
                          <div className="flex items-center">
                            <div className="flex -space-x-2">
                              {audit.assignedUsers.slice(0, 3).map((assignee) => (
                                  <div
                                    key={assignee.userId}
                                    className="ring-2 ring-background rounded-full"
                                  >
                                    <Avatar className="h-6 w-6">
                                      <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                                        {getUserInitials(assignee.userName)}
                                      </AvatarFallback>
                                    </Avatar>
                                  </div>
                              ))}
                              {audit.assignedUsers.length > 3 && (
                                <div className="ring-2 ring-background rounded-full">
                                  <Avatar className="h-6 w-6">
                                    <AvatarFallback className="text-xs bg-muted text-muted-foreground">
                                      +{audit.assignedUsers.length - 3}
                                    </AvatarFallback>
                                  </Avatar>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-500 dark:text-slate-400">Unassigned</span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
